import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { useSearchParams, useNavigate } from 'react-router'
import { AxiosError } from 'axios'
import { authService } from '@/services/api'
import { useAuthStore } from '@/stores'
import { acceptInviteFormSchema, defaultAcceptInviteValues } from './AcceptInviteForm.static'
import type { AcceptInviteFormValues } from './AcceptInviteForm.static'
import type { ApiError, AuthResponse } from '@/types'

export function useAcceptInviteForm() {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [isSuccess, setIsSuccess] = useState(false)
  const setAuth = useAuthStore((s) => s.setAuth)

  const token = searchParams.get('token')

  const form = useForm<AcceptInviteFormValues>({
    resolver: zodResolver(acceptInviteFormSchema),
    defaultValues: defaultAcceptInviteValues,
    mode: 'onBlur',
  })

  const acceptInviteMutation = useMutation({
    mutationFn: (data: { token: string; password: string }) => authService.acceptInvite(data),
    onSuccess: (data: AuthResponse) => {
      setAuth(data.user, data.facility)
      setIsSuccess(true)
      setTimeout(() => navigate('/dashboard'), 1500)
    },
    onError: (error: AxiosError<ApiError>) => {
      const status = error.response?.status
      const message =
        status === 410 || status === 404
          ? t('invitationExpired')
          : status === 429
            ? t('errors.rateLimitExceeded')
            : t('errors.resetPasswordFailed')
      form.setError('root', { type: 'manual', message })
    },
  })

  const onSubmit = (data: AcceptInviteFormValues) => {
    if (!token) return
    acceptInviteMutation.mutate({ token, password: data.password })
  }

  return {
    t,
    form,
    token,
    isLoading: acceptInviteMutation.isPending,
    isSuccess,
    onSubmit,
  }
}
